import type { QueryLazyOptions } from '@apollo/client';
import { useQuery } from '@apollo/client';
import styled from '@emotion/styled';
import { useResource } from '@landsbankinn/l16n';
import { Button, theme } from '@landsbankinn/ui';
import { LandsbankinnContext } from '@landsbankinn/utils';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { animated, useTransition } from 'react-spring';
import { ConsultationType } from '../../../../../__generated__/globalTypes';
import type {
  appointmentServicesAndBranches,
  appointmentServicesAndBranchesVariables,
} from '../../../../graphql/Appointment/__generated__/appointmentServicesAndBranches';
import type { availableDatesAndTimesVariables } from '../../../../graphql/Appointment/__generated__/availableDatesAndTimes';
import { APPOINTMENT_SERVICES_AND_BRANCHES } from '../../../../graphql/Appointment/appointmentServicesAndBranches';
import { AppointmentStep, useAppointmentContext } from '../../BookAppointment';
import { FormContainer } from '../../shared/FormContainer';
import { AppointmentSelectAccess } from './AppointmentSelectAccess';
import { AppointmentSelectBranch } from './AppointmentSelectBranch';
import { AppointmentSelectService } from './AppointmentSelectService';
import { SelectCustomerType } from './SelectCustomerType';
import res from './SelectService.res';

const StyledButton = styled(Button)`
  width: 100%;
`;

const InputContainer = styled(animated.div)`
  margin-bottom: 20px;
  position: relative;
`;

const InputsContainer = styled.div`
  min-height: 412px;
`;

interface IProps {
  defaultServiceId?: string;
  fetchDatesAndTime: (
    options?: QueryLazyOptions<availableDatesAndTimesVariables>,
  ) => void;
  loadingDateAndTime: boolean;
}

enum Steps {
  CustomerType = 'customerType',
  SelectService = 'selectService',
  SelectAccess = 'selectAccess',
  SelectBranch = 'selectBranch',
}

export function SelectService({
  defaultServiceId,
  fetchDatesAndTime,
  loadingDateAndTime,
}: IProps) {
  const [steps, setSteps] = useState<Steps[]>([Steps.CustomerType]);
  const { client } = useContext(LandsbankinnContext);
  const { locale } = useResource();
  const { state, setCurrentStep, dispatch } = useAppointmentContext();
  const { r } = useResource();

  const { customerType } = state;
  const { data } = useQuery<
    appointmentServicesAndBranches,
    appointmentServicesAndBranchesVariables
  >(APPOINTMENT_SERVICES_AND_BRANCHES, {
    client,
    fetchPolicy: 'cache-first',
    variables: {
      customerType: customerType,
    },
    context: {
      headers: {
        'Accept-Language': locale === 'is' ? 'is' : 'en-GB',
      },
    },
    onCompleted: (data) => {
      if (!state.service) {
        if (defaultServiceId) {
          const defaultService = data.consultationServices.find(
            (i) => i.id === defaultServiceId,
          );
          if (defaultService) {
            dispatch?.({
              type: 'selectService',
              service: defaultService,
            });
            return;
          }
        }
        if (data.consultationServices.length === 1) {
          dispatch?.({
            type: 'selectService',
            service: data.consultationServices[0],
          });
        }
      }
    },
  });
  const transitions = useTransition(steps, {
    config: { mass: 2, tension: 2000, friction: 200 },
    from: { opacity: 0, x: '508px' },
    enter: { opacity: 1, x: '0px' },
    leave: { opacity: 0, x: '508px' },
  });

  useEffect(() => {
    const newSteps = [Steps.CustomerType];
    if (data?.consultationServices) {
      newSteps.push(Steps.SelectService);
    }
    if (state.service) {
      newSteps.push(Steps.SelectAccess);
    }
    if (state.serviceType && !state.skipLoaction) {
      newSteps.push(Steps.SelectBranch);
    }
    setSteps(newSteps);
  }, [state, data, setSteps]);

  const variables = React.useMemo(
    () => ({
      variables: {
        serviceLocationId: state?.serviceLocationId ?? '',
        consultationId: state?.service?.id ?? '',
        consultationType: state?.serviceType ?? ConsultationType.Appointment,
      },
    }),
    [state?.service?.id, state?.serviceLocationId, state?.serviceType],
  );

  const onSubmit = useCallback(
    () => fetchDatesAndTime(variables),
    [fetchDatesAndTime, variables],
  );

  return (
    <FormContainer
      onBack={() => setCurrentStep?.(AppointmentStep.Intro)}
      onSubmit={onSubmit}
    >
      <>
        <InputsContainer>
          {transitions(({ x, ...rest }, item) => (
            <InputContainer
              key={item}
              style={{
                ...rest,
                transform: x.to((x) =>
                  x === '0px' ? 'initial' : `translate3d(${x},0,0)`,
                ),
              }}
            >
              {item === Steps.CustomerType && <SelectCustomerType />}

              {data?.consultationServices && item === Steps.SelectService && (
                <AppointmentSelectService
                  services={data.consultationServices}
                />
              )}

              {item === Steps.SelectAccess && <AppointmentSelectAccess />}
              {item === Steps.SelectBranch && <AppointmentSelectBranch />}
            </InputContainer>
          ))}
        </InputsContainer>

        <StyledButton
          title={r(res.continue)}
          type="submit"
          loading={loadingDateAndTime}
          bg={theme.colors.primary}
        />
      </>
    </FormContainer>
  );
}
